import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Euro, FileText, ShoppingBag, Users, Star, Tag, Send, MessageCircle, BarChart2, TrendingUp, ArrowRight } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import { formatDate } from '../utils/dateUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function KPICard({ icon, color, label, value, sub, to }) {
  const inner = (
    <div className="p-5 rounded-2xl h-full transition-all hover:shadow-md"
      style={{ background: 'var(--admin-surface)', border: `1px solid ${color}20` }}>
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}15`, color }}>{icon}</div>
        {to && <ArrowRight size={14} style={{ color: 'var(--admin-faint)' }} />}
      </div>
      <p className="text-2xl font-bold mb-0.5" style={{ color }}>{value ?? '—'}</p>
      <p className="text-sm font-semibold mb-0.5" style={{ color: 'var(--admin-text)' }}>{label}</p>
      {sub && <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>{sub}</p>}
    </div>
  );
  return to ? <Link to={to} className="block">{inner}</Link> : inner;
}

export default function AdminCommerceHub() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ana, devis, boutique, fidele] = await Promise.all([
        fetch(`${BACKEND}/api/analytics/overview`, { headers: H }).then(r => r.json()).catch(() => ({})),
        fetch(`${BACKEND}/api/devis?limit=5`, { headers: H }).then(r => r.json()).catch(() => ({})),
        fetch(`${BACKEND}/api/boutique/admin/stats`, { headers: H }).then(r => r.json()).catch(() => ({})),
        fetch(`${BACKEND}/api/admin/fidelite/stats`, { headers: H }).then(r => r.json()).catch(() => ({})),
      ]);
      setStats({ ana: ana.kpis || {}, devis, boutique, fidele });
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
    setLoading(false);
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  const kpis = [
    { icon: <Euro size={18}/>, color: 'var(--admin-gold)', label: 'CA encaissé', value: stats?.ana?.ca_total ? `${stats.ana.ca_total.toFixed(0)}€` : null, sub: 'Locations + boutique', to: '/admin/analytics' },
    { icon: <FileText size={18}/>, color: 'var(--admin-blue)', label: 'Devis en cours', value: stats?.devis?.quotes?.length, sub: `${stats?.devis?.quotes?.filter(q=>q.statut==='envoye').length||0} à relancer`, to: '/admin/devis' },
    { icon: <ShoppingBag size={18}/>, color: 'var(--admin-green)', label: 'CA Boutique', value: stats?.boutique?.ca_boutique ? `${stats.boutique.ca_boutique.toFixed(0)}€` : '0€', sub: `${stats?.boutique?.commandes_payees||0} commandes payées`, to: '/admin/boutique' },
    { icon: <Star size={18}/>, color: 'var(--admin-amber)', label: 'Clients fidèles', value: stats?.fidele?.total_clients, sub: `${stats?.fidele?.cashback_en_attente?.toFixed(0)||0}€ cashback disponible`, to: '/admin/fidelite' },
  ];

  const shortcuts = [
    { to: '/admin/devis', icon: <FileText size={14}/>, label: 'Nouveaux devis', color: 'var(--admin-blue)' },
    { to: '/admin/boutique', icon: <ShoppingBag size={14}/>, label: 'Commandes', color: 'var(--admin-green)' },
    { to: '/admin/tarifs', icon: <Tag size={14}/>, label: 'Modifier tarifs', color: 'var(--admin-gold)' },
    { to: '/admin/fidelite', icon: <Star size={14}/>, label: 'Fidélité', color: 'var(--admin-amber)' },
    { to: '/admin/messages', icon: <MessageCircle size={14}/>, label: 'Messages', color: 'var(--admin-teal)' },
    { to: '/admin/relances-auto', icon: <Send size={14}/>, label: 'Relances', color: 'var(--admin-purple)' },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--admin-gold)' }}>🏪 Commerce</h1>
        <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>Devis, boutique, clients, fidélité et tarifs</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-32 rounded-2xl" style={{ background: 'var(--admin-surface)' }} />)}</div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {kpis.map((k, i) => <KPICard key={i} {...k} />)}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {shortcuts.map((s, i) => (
          <Link key={i} to={s.to} className="flex items-center gap-2 p-3 rounded-xl transition-all hover:opacity-80"
            style={{ background: 'var(--admin-surface)', border: `1px solid ${s.color}20` }}>
            <span style={{ color: s.color }}>{s.icon}</span>
            <span className="text-xs font-medium" style={{ color: 'var(--admin-text)' }}>{s.label}</span>
          </Link>
        ))}
      </div>

      {/* Derniers devis */}
      {stats?.devis?.quotes?.length > 0 && (
        <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
          <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid var(--admin-border-soft)' }}>
            <h2 className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>Derniers devis</h2>
            <Link to="/admin/devis" className="text-xs" style={{ color: 'var(--admin-gold)' }}>Tout voir →</Link>
          </div>
          <div>
            {(stats.devis.quotes || []).slice(0,5).map((q, i) => (
              <div key={q.id} className="flex items-center gap-4 px-5 py-3" style={{ borderBottom: i < 4 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate" style={{ color: 'var(--admin-text)' }}>{q.client_nom || q.client_email}</p>
                  <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>{q.numero} · {formatDate(q.created_at)}</p>
                </div>
                <span className="font-bold text-sm" style={{ color: 'var(--admin-gold)' }}>{q.total?.toFixed(0)}€</span>
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--admin-gold-dim)', color: 'var(--admin-gold)' }}>{q.statut}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
